import MaskedView from '@euler/components/MaskedView';
import { usePersistFn } from '@euler/utils/hooks';
import { LinearGradient } from 'expo-linear-gradient';
import React, { memo, ReactNode, useEffect, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleProp,
  ViewStyle,
} from 'react-native';

type Orientation = 'horizontal' | 'vertical';
type Point = { x: number; y: number };

const gradientAngleProps: { [p in Orientation]: { start: Point; end: Point } } =
  {
    horizontal: {
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 },
    },
    vertical: {
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
    },
  };

export const ListMask = memo(
  (props: {
    style?: StyleProp<ViewStyle>;
    leadingMask: boolean;
    trailingMask: boolean;
    location?: number;
    enabled?: boolean;
    children: ReactNode;
    orientation?: Orientation;
  }) => {
    const { style, children } = props;
    const location = props.location ?? 0.1;
    const orientation = props.orientation ?? 'horizontal';
    if (props.enabled === false) {
      return <>{children}</>;
    }
    return (
      <MaskedView
        style={style}
        maskElement={
          <LinearGradient
            colors={['transparent', 'black', 'black', 'transparent']}
            {...gradientAngleProps[orientation]}
            locations={[
              0,
              props.leadingMask ? location : 0,
              props.trailingMask ? 1 - location : 1,
              1,
            ]}
            css={`
              flex: 1;
            `}
          />
        }
      >
        {children}
      </MaskedView>
    );
  },
);

export const useScrollableMask = (props: {
  orientation: Orientation;
  onContentSizeChange?: (width: number, height: number) => void;
  onScroll?: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onLayout?: (e: LayoutChangeEvent) => void;
}) => {
  const orientation = props.orientation;

  const [showLeadingMask, setShowLeadingMask] = useState(false);
  const [showTrailingMask, setShowTrailingMask] = useState(true);
  const [contentLength, setContentLength] = useState(0);
  const [containerLength, setContainerLength] = useState(0);
  const contentOffset = useRef<number>(0);

  const onLayout = usePersistFn((e: LayoutChangeEvent) => {
    setContainerLength(
      orientation == 'horizontal'
        ? e.nativeEvent.layout.width
        : e.nativeEvent.layout.height,
    );
    props.onLayout?.(e);
  });

  const onContentSizeChange = usePersistFn((width: number, height: number) => {
    setContentLength(orientation === 'horizontal' ? width : height);
    props.onContentSizeChange?.(width, height);
  });

  const onScroll = usePersistFn(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset =
        orientation === 'horizontal'
          ? e.nativeEvent.contentOffset.x
          : e.nativeEvent.contentOffset.y;
      contentOffset.current = offset;
      const isLeadingMaskVisible = offset > 0;
      const isTrailingMaskVisible = offset + containerLength < contentLength;
      if (showLeadingMask !== isLeadingMaskVisible) {
        setShowLeadingMask(isLeadingMaskVisible);
      }
      if (showTrailingMask !== isTrailingMaskVisible) {
        setShowTrailingMask(isTrailingMaskVisible);
      }
      props.onScroll?.(e);
    },
  );

  // `onScroll` only fires while the user scrolls, so content that fits the
  // viewport (no scroll ever happens) would leave the trailing mask stuck on
  // from its initial `true`. Recompute both edges whenever the measured sizes
  // change so a non-overflowing strip shows no fade.
  useEffect(() => {
    if (containerLength === 0) return;
    const offset = contentOffset.current;
    setShowLeadingMask(offset > 0);
    setShowTrailingMask(offset + containerLength < contentLength);
  }, [contentLength, containerLength]);

  return {
    showLeadingMask,
    showTrailingMask,
    onLayout,
    onContentSizeChange,
    onScroll,
  };
};
